/**
 * Datas no fuso do Brasil.
 *
 * A aplicação Flask antiga tinha `brasil_hoje_date()` porque os KPIs diários
 * saíam deslocados quando calculados em UTC. Mantemos a mesma semântica: tudo o
 * que é "dia civil" (streak, meta diária, planner) usa America/Sao_Paulo;
 * tudo o que é instante (expiração, pagamentos) fica em UTC.
 */

export const TZ_BRASIL = 'America/Sao_Paulo';

const dayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: TZ_BRASIL,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Data civil no Brasil em `YYYY-MM-DD`. */
export function brasilDayString(at: Date = new Date()): string {
  return dayFormatter.format(at);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * 86_400_000);
}

export function daysBetween(from: Date, to: Date): number {
  return Math.round((to.getTime() - from.getTime()) / 86_400_000);
}

/** Formata para exibição, ex.: "15/03/2026 14:32". */
export function formatDateTimeBR(date: Date | string | null): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ_BRASIL,
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(d);
}

export function formatDateBR(date: Date | string | null): string {
  if (!date) return '—';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: TZ_BRASIL,
    dateStyle: 'short',
  }).format(d);
}

/**
 * Converte o formato datetime do SQLite legado (`YYYY-MM-DD HH:MM:SS[.ffffff]`,
 * gravado sem informação de fuso pelo SQLAlchemy) num `Date` em UTC.
 */
export function parseSqliteDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const iso = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  const withZone = /[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
  const d = new Date(withZone);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Serializa para o formato aceite pela base legada. */
export function toSqliteDate(date: Date): string {
  return date.toISOString().replace('T', ' ').replace('Z', '').slice(0, 26);
}

/**
 * Datetime “naive” no fuso de Brasília, como o Flask gravava em `simulado_logs`
 * e `logs_reviews`. Filtros por dia civil comparam o prefixo `YYYY-MM-DD`.
 */
export function toSqliteDateBrasil(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: TZ_BRASIL,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  })
    .format(date)
    .replace('T', ' ');
}


/** Soma dias civis a uma data `YYYY-MM-DD` (sem depender de fuso). */
export function somarDiasBrasil(diaIso: string, dias: number): string {
  const [y, m, d] = diaIso.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + dias);
  return dt.toISOString().slice(0, 10);
}

/**
 * Meia-noite civil no Brasil para `diaIso`, convertida para `Date` UTC.
 *
 * America/Sao_Paulo está fixo em UTC−3 desde 2019 (sem horário de verão).
 * Meia-noite BRT de `diaIso` = `diaIso` 03:00:00.000Z.
 */
export function inicioDoDiaBrasilEmUtc(diaIso: string): Date {
  const [year, month, day] = diaIso.split('-').map(Number) as [
    number,
    number,
    number,
  ];
  return new Date(Date.UTC(year, month - 1, day, 3, 0, 0, 0));
}

/** 23:59:59.999 do dia civil brasileiro, em UTC. */
export function fimDoDiaBrasilEmUtc(diaIso: string): Date {
  const inicioSeguinte = inicioDoDiaBrasilEmUtc(somarDiasBrasil(diaIso, 1));
  return new Date(inicioSeguinte.getTime() - 1);
}

/**
 * Limite para contar cards “vencidos” / “vence hoje”.
 *
 * Cards com `proxima_revisao <= limite` entram em “revisar”. O limite é o fim
 * do dia civil BR (mais `janelaDias` no modo revisão inteligente), não `now`.
 */
export function limiteVencimentoRevisao(now: Date, janelaDias = 0): Date {
  const diaAlvo = somarDiasBrasil(brasilDayString(now), janelaDias);
  return fimDoDiaBrasilEmUtc(diaAlvo);
}
